/**
 * Customer groups API client (BFF: `/api/v1/catalog/pricing/customer-groups`) — epic-pricing-002
 * Phase 1, plan-platform-002 WP4.3. Confirmed live against `web-merchant-portal-bff`'s
 * `openapi.yml` (2026-09-03): list/create/update + members list/add/remove.
 *
 * **Contract gaps (disclosed):**
 *  - No `GET .../customer-groups/{code}` (single-group fetch) — `CustomerGroupsPanel`'s edit dialog
 *    is seeded from the row object already held by the list query, never a fresh per-code fetch
 *    (the same approach `ChannelsPanel`'s edit flow already uses for the same reason).
 *  - No `DELETE .../customer-groups/{code}` — the exact archive-only gap price lists hit in WP4.2.
 *    `CustomerGroupsPanel` has no delete action; `active:false` via {@link updateCustomerGroup} is
 *    the reachable terminal state, mirroring the price-list kill-switch precedent.
 *  - `CustomerGroupRequest.code` is schema-required on the PUT body too (the same shape as POST),
 *    with no "ignored on update" disclaimer unlike `PriceListRequest.code`. To stay safe this
 *    module's callers always resend the group's own existing code unchanged on update — the UI
 *    never offers to edit it (mirroring the price-list code-immutable-on-edit convention).
 */
import { apiClient } from '@/shared/api';

import type {
  CustomerGroupMemberRequest,
  CustomerGroupMemberResponse,
  CustomerGroupRequest,
  CustomerGroupResponse,
} from '../model/customerGroupTypes';

const BASE = '/api/v1/catalog/pricing/customer-groups';

export const listCustomerGroups = async (): Promise<CustomerGroupResponse[]> =>
  (await apiClient.get<CustomerGroupResponse[]>(BASE)).data;

export const createCustomerGroup = async (
  body: CustomerGroupRequest,
): Promise<CustomerGroupResponse> => (await apiClient.post<CustomerGroupResponse>(BASE, body)).data;

export const updateCustomerGroup = async (
  code: string,
  body: CustomerGroupRequest,
): Promise<CustomerGroupResponse> =>
  (await apiClient.put<CustomerGroupResponse>(`${BASE}/${encodeURIComponent(code)}`, body)).data;

export const listCustomerGroupMembers = async (
  code: string,
): Promise<CustomerGroupMemberResponse[]> =>
  (
    await apiClient.get<CustomerGroupMemberResponse[]>(`${BASE}/${encodeURIComponent(code)}/members`)
  ).data;

/** Idempotent — adding an already-present member is a no-op `204`. */
export const addCustomerGroupMember = async (
  code: string,
  body: CustomerGroupMemberRequest,
): Promise<void> => {
  await apiClient.post(`${BASE}/${encodeURIComponent(code)}/members`, body);
};

export const removeCustomerGroupMember = async (
  code: string,
  member: CustomerGroupMemberRequest,
): Promise<void> => {
  await apiClient.delete(
    `${BASE}/${encodeURIComponent(code)}/members/${encodeURIComponent(member.customerIdentityKind)}/${encodeURIComponent(member.customerIdentityId)}`,
  );
};
